import classNames from 'classnames';
import { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Slick from 'react-slick';
import Image from '@alltrails/core/components/Image';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import Button from '@alltrails/denali/components/Button';
import IconButton from '@alltrails/denali/components/IconButton';
import Link from '@alltrails/denali/components/Link';
import ArrowRec from '@alltrails/denali/icons/ArrowRec';
import useDeeplink from '@alltrails/install-prompt/hooks/useDeeplink';
import { APP_STORE_URL_MAP } from '@alltrails/layout/components/Footer/StoreBadges/AppStore';
import { GOOGLE_PLAY_URL_MAP } from '@alltrails/layout/components/Footer/StoreBadges/GooglePlay';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import styles from './ExploreAndDiscover.module.scss';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import qrCodeSrc from './qr-code.svg';

// https://www.figma.com/design/SLeQY3u3PIQLGina96uVji/2025-Web-Pages?node-id=6982-4022&m=dev
const ExploreAndDiscover = () => {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();

  const slides = [
    {
      alt: intl.formatMessage({
        defaultMessage: 'A mobile phone displays a review summary of a trail.'
      }),
      // eslint-disable-next-line @typescript-eslint/no-require-imports, import/no-dynamic-require
      avif: require(`./Features-Block-Reviews-${languageRegionCode.toUpperCase()}-D.avif`),
      // eslint-disable-next-line @typescript-eslint/no-require-imports, import/no-dynamic-require
      png: require(`./Features-Block-Reviews-${languageRegionCode.toUpperCase()}-D.png`),
      text: (
        <FormattedMessage defaultMessage="All our trails are verified by experts and reviewed by our global community of adventurers like you." />
      ),
      title: <FormattedMessage defaultMessage="Pick the right trail for your day" />
    },
    {
      alt: intl.formatMessage({
        defaultMessage:
          'A mobile phone displays tracking metrics such as total time, distance, elevation gain, and pace. At the bottom, three upcoming points of interest are displayed.'
      }),
      // eslint-disable-next-line @typescript-eslint/no-require-imports, import/no-dynamic-require
      avif: require(`./Features-Block-Navigate-${languageRegionCode.toUpperCase()}-D.avif`),
      // eslint-disable-next-line @typescript-eslint/no-require-imports, import/no-dynamic-require
      png: require(`./Features-Block-Navigate-${languageRegionCode.toUpperCase()}-D.png`),
      text: (
        <FormattedMessage defaultMessage="Out on the trail, you can keep an eye on your planned route, along with elevation changes and waypoints." />
      ),
      title: <FormattedMessage defaultMessage="Navigate & track" />
    },
    {
      alt: intl.formatMessage({
        defaultMessage: 'A mobile phone displays a saved group of trail lists.'
      }),
      // eslint-disable-next-line @typescript-eslint/no-require-imports, import/no-dynamic-require
      avif: require(`./Features-Block-Saved-${languageRegionCode.toUpperCase()}-D.avif`),
      // eslint-disable-next-line @typescript-eslint/no-require-imports, import/no-dynamic-require
      png: require(`./Features-Block-Saved-${languageRegionCode.toUpperCase()}-D.png`),
      text: <FormattedMessage defaultMessage="Log all your trails in one place, and share insights to inspire and inform fellow adventurers." />,
      title: <FormattedMessage defaultMessage="Keep track of your travels" />
    }
  ];

  const getDeeplink = useDeeplink({});
  const deeplink = getDeeplink();
  const carouselRef = useRef<Slick>(undefined);

  // these are same width badges and different from storeBadges/AppStore and storeBadges/GooglePlay
  // eslint-disable-next-line @typescript-eslint/no-require-imports, import/no-dynamic-require
  const appleSrc = require(`./app-apple-${languageRegionCode}.svg`).default;
  // eslint-disable-next-line @typescript-eslint/no-require-imports, import/no-dynamic-require
  const googleSrc = require(`./app-google-${languageRegionCode}.svg`).default;

  const [currentNav, setCurrentNav] = useState(0);
  const [isDesktopMode, setIsDesktopMode] = useState(false);

  const slide = slides[currentNav];

  const handleNavClick = (index: number) => {
    setIsDesktopMode(true);
    setCurrentNav(index);
  };

  const handlePrevNextClick = (event: React.MouseEvent, previousOrNext: string) => {
    let previousOrNextNav = currentNav + (previousOrNext === 'previous' ? -1 : 1);

    if (previousOrNextNav < 0) {
      previousOrNextNav = 2;
    } else if (currentNav >= 2 && previousOrNext === 'next') {
      previousOrNextNav = 0;
    }

    setCurrentNav(previousOrNextNav);

    setIsDesktopMode(true);
  };

  const SLIDE_NAVS = [
    intl.formatMessage({ defaultMessage: 'Search & discover' }),
    intl.formatMessage({ defaultMessage: 'Navigate & track' }),
    intl.formatMessage({ defaultMessage: 'Remember & inspire' })
  ];

  const newSlides = isDesktopMode ? [slides[currentNav]] : slides;

  return (
    <div className={styles.container}>
      <div className={styles.nav}>
        <IconButton
          icon={{ Component: ArrowRec }}
          onClick={e => handlePrevNextClick(e, 'previous')}
          title={intl.formatMessage({ defaultMessage: 'Navigate previous' })}
          size="lg"
          variant="transparent"
        />
        <div className={styles.navs}>
          <Link className={classNames(styles.navLink, { [styles.selected]: currentNav === 0 })} onClick={() => handleNavClick(0)} size="md">
            {SLIDE_NAVS[0]}
          </Link>
          <Link className={classNames(styles.navLink, { [styles.selected]: currentNav === 1 })} onClick={() => handleNavClick(1)} size="md">
            {SLIDE_NAVS[1]}
          </Link>
          <Link className={classNames(styles.navLink, { [styles.selected]: currentNav === 2 })} onClick={() => handleNavClick(2)} size="md">
            {SLIDE_NAVS[2]}
          </Link>
        </div>
        <IconButton
          icon={{ Component: ArrowRec, orientation: 'down' }}
          onClick={e => handlePrevNextClick(e, 'next')}
          title={intl.formatMessage({ defaultMessage: 'Navigate next' })}
          size="lg"
          variant="transparent"
        />
      </div>
      <Slick
        ref={carouselRef}
        afterChange={(index: number) => {
          setCurrentNav(index);
        }}
        arrows={false}
        responsive={[
          {
            breakpoint: 999999,
            settings: 'unslick'
          },
          { breakpoint: 1120, settings: {} }
        ]}
      >
        {newSlides.map((slide, index) => (
          <div className={styles.screen} key={index}>
            <picture>
              <source srcSet={slide.avif.default.src} type="image/avif" />
              <img className={styles.image} src={slide.png} alt={slide.alt} loading="lazy" />
            </picture>
          </div>
        ))}
      </Slick>
      <div className={styles.feature}>
        <div className={styles.dots}>
          {[...Array(slides.length)].map((_, i) => (
            <button
              key={SLIDE_NAVS[i]}
              type="button"
              aria-label={`${SLIDE_NAVS[i]}`}
              className={`${styles.dot} ${currentNav === i ? styles.active : ''}`}
              onClick={() => {
                carouselRef?.current?.slickGoTo(i);
              }}
            />
          ))}
        </div>
        <Link
          className={styles.link}
          onClick={() => {
            carouselRef?.current?.slickNext();
          }}
          size="sm"
        >
          {SLIDE_NAVS[currentNav]}
        </Link>
        <h2 className={styles.title}>{slide.title}</h2>
        <p className={styles.featureText}>{slide.text}</p>
        <div className={styles.extra}>
          <Image
            alt={intl.formatMessage({
              defaultMessage:
                'This QR code is intended to scan from a smartphone. If you have the AllTrails app, the link will open the app for you. If you don’t have the app, the link will take you to your device’s app store.'
            })}
            className={styles.qr}
            src={qrCodeSrc}
          />
          <div className={styles.badges}>
            <a href={GOOGLE_PLAY_URL_MAP[languageRegionCode as LanguageRegionCode]} target="_blank" rel="noreferrer">
              <Image
                alt={intl.formatMessage({ defaultMessage: 'App Store - Trail Guides & Maps for Hiking, Camping, and Running | AllTrails' })}
                src={appleSrc}
              />
            </a>
            <a href={APP_STORE_URL_MAP[languageRegionCode as LanguageRegionCode]} target="_blank" rel="noreferrer">
              <Image
                alt={intl.formatMessage({ defaultMessage: 'Google Play - Trail Guides & Maps for Hiking, Camping, and Running | AllTrails' })}
                src={googleSrc}
              />
            </a>
          </div>
        </div>
        <Button
          className={styles.button}
          linkInfo={{ href: deeplink }}
          text={<FormattedMessage defaultMessage="Get the free app" />}
          variant="primary"
        />
      </div>
    </div>
  );
};

export default ExploreAndDiscover;
